import { Temporal } from 'temporal-polyfill';
import { MultiMap } from './common-utils.js';
import { AvailabilityToDateTime, DayOfWeek, MinimalAvailability, TimeZoneAdjustment } from './data/index.js';
import { dayOfWeekFromTemporal, nextPlainDateOnDayOfWeek } from './scheduling-utils.js';
import { formatPlainTime } from './temporal-utils/formatting.js';

function plainDateOnOrAfterDayOfWeek(plainDate: Temporal.PlainDate, targetDay: DayOfWeek): Temporal.PlainDate {
  return dayOfWeekFromTemporal(plainDate) === targetDay ? plainDate : nextPlainDateOnDayOfWeek(plainDate, targetDay);
}

export function groupCoachAvailabilitiesByDay<T extends MinimalAvailability>(
  availabilities: T[],
): MultiMap<DayOfWeek, T> {
  return MultiMap.groupBy(availabilities, ({ day }) => day);
}

/**
 * Adjust a given availability by converting its plain time and day of week from the source time zone to the target time zone
 */
export function adjustAvailabilityByTimeZone(
  { day, startPlainTime, endPlainTime }: MinimalAvailability,
  {
    sourceTimeZone,
    targetTimeZone,
    referencePlainDate,
  }: TimeZoneAdjustment & { referencePlainDate?: Temporal.PlainDate },
): AvailabilityToDateTime {
  const plainDate = referencePlainDate
    ? plainDateOnOrAfterDayOfWeek(referencePlainDate, day)
    : nextPlainDateOnDayOfWeek(Temporal.Now.plainDateISO('UTC'), day);

  const startZoned = plainDate.toZonedDateTime({ plainTime: startPlainTime, timeZone: sourceTimeZone });
  const endZoned = plainDate.toZonedDateTime({ plainTime: endPlainTime, timeZone: sourceTimeZone });

  const startWithTimeZone = startZoned.withTimeZone(targetTimeZone);
  const endWithTimeZone = endZoned.withTimeZone(targetTimeZone);

  const startAdjustedPlainDate = startWithTimeZone.toPlainDate();
  const endAdjustedPlainDate = endWithTimeZone.toPlainDate();

  const startAdjustedPlainTime = startWithTimeZone.toPlainTime();
  const endAdjustedPlainTime = endWithTimeZone.toPlainTime();

  return {
    startWithTimeZone,
    endWithTimeZone,
    startAdjustedPlainDate,
    endAdjustedPlainDate,
    startAdjustedPlainTime,
    endAdjustedPlainTime,
    adjustedDay: dayOfWeekFromTemporal(startAdjustedPlainDate),
  };
}

/**
 * Convert a meeting block from `sourceTimeZone` to `targetTimeZone`.
 * Accepts plain-time strings (e.g. from SQL) or `Temporal.PlainTime` values.
 */
export function adjustMeetingBlockToTZ(
  {
    day,
    startPlainTime,
    endPlainTime,
  }: {
    day: DayOfWeek;
    startPlainTime: string | Temporal.PlainTime;
    endPlainTime: string | Temporal.PlainTime;
  },
  sourceTimeZone: string,
  targetTimeZone: string,
  referencePlainDate?: Temporal.PlainDate,
): AvailabilityToDateTime {
  return adjustAvailabilityByTimeZone(
    {
      day,
      startPlainTime:
        typeof startPlainTime === 'string' ? Temporal.PlainTime.from(startPlainTime) : startPlainTime,
      endPlainTime: typeof endPlainTime === 'string' ? Temporal.PlainTime.from(endPlainTime) : endPlainTime,
    },
    {
      sourceTimeZone,
      targetTimeZone,
      referencePlainDate,
    },
  );
}

export function coachTimeLabelsFromMeetingBlock(
  meetingBlock: MinimalAvailability,
  coachTimeZone: string,
  schoolOrgTimeZone: string,
  referencePlainDate?: Temporal.PlainDate,
): { startTime: string; endTime: string } {
  const adjusted = adjustAvailabilityByTimeZone(meetingBlock, {
    sourceTimeZone: schoolOrgTimeZone,
    targetTimeZone: coachTimeZone,
    referencePlainDate,
  });
  return {
    startTime: formatPlainTime(adjusted.startAdjustedPlainTime, { hour12: true }).toLowerCase(),
    endTime: formatPlainTime(adjusted.endAdjustedPlainTime, { hour12: true }).toLowerCase(),
  };
}

type PlainTimeRange = {
  startPlainTime: Temporal.PlainTime;
  endPlainTime: Temporal.PlainTime;
};

/**
 * True if `coverageBlocks` (same calendar day, already filtered) fully cover
 * `[targetStart, targetEnd)` — including across adjacent blocks with at most a 1-minute gap.
 * Matches assignment eligibility (`isAvailableForIntervalMultiBlock`).
 */
export function coversPlainTimeInterval(
  coverageBlocks: ReadonlyArray<PlainTimeRange>,
  targetStart: Temporal.PlainTime,
  targetEnd: Temporal.PlainTime,
): boolean {
  if (coverageBlocks.length === 0) {
    return false;
  }

  const sorted = [...coverageBlocks].sort((a, b) => Temporal.PlainTime.compare(a.startPlainTime, b.startPlainTime));

  let currentTime = targetStart;

  for (const { startPlainTime, endPlainTime } of sorted) {
    if (Temporal.PlainTime.compare(startPlainTime, currentTime) > 0) {
      const gapMinutes = startPlainTime.since(currentTime).total('minute');
      if (gapMinutes > 1) {
        return false;
      }
    }

    if (Temporal.PlainTime.compare(endPlainTime, currentTime) > 0) {
      currentTime = Temporal.PlainTime.compare(endPlainTime, targetEnd) > 0 ? targetEnd : endPlainTime;
      if (Temporal.PlainTime.compare(currentTime, targetEnd) >= 0) {
        return true;
      }
    }
  }

  return false;
}
